import React, { ReactNode, createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { pdf } from '@react-pdf/renderer';

import { PdfDocument } from '../components/workspace/pdf/PdfDocument';
import type { MeasurementData } from '../store/reducers/workspace.reducer.ts';
import type { Project, Version } from '../types/project';
import type { Norm } from '../types/user';
import type { UnitSystem } from '../types/util.ts';
import type { LuminairePdfDisplay } from '../utils/pdfLuminaireDisplay';
import type { PhotometrySelection } from '../utils/pdfPlotGeneration/photometryMapping';
import { pdfWorkerManager } from '../workers/pdfWorkerManager';

/* eslint-disable react-refresh/only-export-components */

export interface PdfCacheProps {
  checkedItems: Record<string, boolean>;
  checkedMeasurementsItems: Record<string, boolean>;
  project: Project;
  activeVersion: Version;
  norms: Norm[];
  reportImageUrls: Record<string, string>;
  frontendReportImageUrls?: Record<string, string>;
  luminaireDisplayByUuid?: Record<string, LuminairePdfDisplay>;
  measurementData: MeasurementData[];
  unitSystem: UnitSystem;
  selectedPhotometries?: PhotometrySelection;
}

interface PdfCacheEntry {
  blob: Blob;
  url: string;
  props: PdfCacheProps;
  createdAt: number;
}

interface PdfCacheContextType {
  getCachedPdf: (props: PdfCacheProps) => Promise<string>;
  isGenerating: boolean;
  clearCache: () => void;
}

const PdfCacheContext = createContext<PdfCacheContextType | null>(null);

/** Stable short fingerprint for data URLs (PNG base64 prefixes are identical). */
function fingerprintUrl(url: string): string {
  let hash = 2166136261;
  for (let i = 0; i < url.length; i++) {
    hash ^= url.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `${url.length}:${(hash >>> 0).toString(36)}`;
}

// Generate a cache key from PDF props
const generateCacheKey = (props: PdfCacheProps): string => {
  // Include sorted URL values so cache invalidates when blob URLs change (e.g. after re-fetch)
  const urlEntries = Object.entries(props.reportImageUrls || {}).sort(([a], [b]) => a.localeCompare(b));
  const urlsFingerprint = urlEntries.map(([id, url]) => `${id}:${url}`).join('|');
  const frontendEntries = Object.entries(props.frontendReportImageUrls || {}).sort(([a], [b]) => a.localeCompare(b));
  const frontendFingerprint = frontendEntries.map(([k, url]) => `${k}:${fingerprintUrl(url)}`).join('|');
  const luminaireEntries = Object.entries(props.luminaireDisplayByUuid || {}).sort(([a], [b]) => a.localeCompare(b));
  const luminaireFingerprint = luminaireEntries
    .map(([uuid, d]) => `${uuid}:${d.manufacturer}:${d.displayName}:${d.tamanName ?? ''}:${d.lumenOutput ?? ''}`)
    .join('|');

  return JSON.stringify({
    checkedItems: props.checkedItems,
    checkedMeasurementsItems: props.checkedMeasurementsItems,
    projectId: props.project?.id,
    versionId: props.activeVersion?.id,
    normsLength: props.norms?.length,
    measurementDataLength: props.measurementData?.length,
    reportImageUrls: urlsFingerprint,
    frontendReportImageUrls: frontendFingerprint,
    luminaireDisplayByUuid: luminaireFingerprint,
    unitSystem: props.unitSystem,
    selectedPhotometries: props.selectedPhotometries,
  });
};

export const PdfCacheProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cache, setCache] = useState<Map<string, PdfCacheEntry>>(new Map());
  const [isGenerating, setIsGenerating] = useState(false);
  const generationPromises = useRef<Map<string, Promise<string>>>(new Map());
  const activeGenerations = useRef(0);

  const setGenerating = useCallback((active: boolean) => {
    if (active) {
      activeGenerations.current += 1;
      setIsGenerating(true);
    } else {
      activeGenerations.current = Math.max(0, activeGenerations.current - 1);
      setIsGenerating(activeGenerations.current > 0);
    }
  }, []);

  const clearCache = useCallback(() => {
    // Clean up blob URLs
    cache.forEach((entry) => {
      if (entry.url) {
        URL.revokeObjectURL(entry.url);
      }
    });
    setCache(new Map());
    generationPromises.current.clear();
  }, [cache]);

  const getCachedPdf = useCallback(
    async (props: PdfCacheProps): Promise<string> => {
      const cacheKey = generateCacheKey(props);

      // Check if already cached
      const cached = cache.get(cacheKey);
      if (cached) {
        return cached.url;
      }

      // Check if generation is already in progress
      const existingPromise = generationPromises.current.get(cacheKey);
      if (existingPromise) {
        return existingPromise;
      }
      const generationPromise = (async () => {
        try {
          setGenerating(true);

          let url: string;
          let blob: Blob;

          // Try to use web worker first, fallback to main thread
          try {
            if (pdfWorkerManager.isWorkerAvailable()) {
              console.log('PdfCache: Using web worker for PDF generation');
              url = await pdfWorkerManager.generatePdf(props);
              // For worker-generated PDFs, we don't have direct access to the blob
              // but we can fetch it from the URL if needed for caching
              const response = await fetch(url);
              blob = await response.blob();
            } else {
              throw new Error('Worker not available');
            }
          } catch (workerError) {
            console.log('PdfCache: Worker failed, generating PDF on main thread', workerError);

            const pdfDocument = (
              <PdfDocument
                checkedItems={props.checkedItems}
                checkedMeasurementsItems={props.checkedMeasurementsItems}
                project={props.project}
                activeVersion={props.activeVersion}
                norms={props.norms}
                reportImageUrls={props.reportImageUrls}
                frontendReportImageUrls={props.frontendReportImageUrls}
                luminaireDisplayByUuid={props.luminaireDisplayByUuid}
                measurementData={props.measurementData}
                unitSystem={props.unitSystem}
                selectedPhotometries={props.selectedPhotometries}
              />
            );

            blob = await pdf(pdfDocument).toBlob();
            url = URL.createObjectURL(blob);
          }

          const entry: PdfCacheEntry = {
            blob,
            url,
            props,
            createdAt: Date.now(),
          };

          setCache((prev) => new Map(prev).set(cacheKey, entry));

          return url;
        } catch (error) {
          console.error('PdfCache: PDF generation failed:', error);
          throw error;
        } finally {
          setGenerating(false);
          generationPromises.current.delete(cacheKey);
        }
      })();

      generationPromises.current.set(cacheKey, generationPromise);
      return generationPromise;
    },
    [cache, setGenerating],
  );

  const contextValue = useMemo(
    () => ({
      getCachedPdf,
      isGenerating,
      clearCache,
    }),
    [getCachedPdf, isGenerating, clearCache],
  );

  return <PdfCacheContext.Provider value={contextValue}>{children}</PdfCacheContext.Provider>;
};

export const usePdfCache = (): PdfCacheContextType => {
  const context = useContext(PdfCacheContext);
  if (!context) {
    throw new Error('usePdfCache must be used within a PdfCacheProvider');
  }
  return context;
};
